
/**
 * Interface for MFA-specific errors with additional context and status information
 */
export interface MfaError extends Error {
  /** Optional error code for more granular error handling */
  code?: string;
  /** HTTP status code if applicable */
  statusCode?: number;
  /** Additional context information for debugging */
  context?: Record<string, unknown>;
}

/**
 * Creates a standardized MFA error with consistent structure
 * 
 * @param message - Human-readable error message
 * @param code - Optional error code for programmatic handling
 * @param statusCode - Optional HTTP status code if applicable
 * @returns Structured MFA error object
 */
export function createMfaError(message: string, code?: string, statusCode?: number): MfaError {
  const error = new Error(message) as MfaError;
  error.name = 'MfaError';
  error.code = code;
  error.statusCode = statusCode;
  return error;
}

/**
 * Handles unknown error types by converting them to standardized MfaError format
 * This provides consistent error handling across the application
 * 
 * @param error - Any error type caught in try/catch blocks
 * @returns Standardized MfaError object
 */
export function handleMfaError(error: unknown): MfaError {
  if (error instanceof Error) {
    return error as MfaError;
  }
  
  if (typeof error === 'string') {
    return createMfaError(error);
  }
  
  return createMfaError('An unknown error occurred');
}
